import { Component, HostListener, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Select, Store } from '@ngxs/store';
import { delay, filter, finalize, map, mapTo, shareReplay, take, takeUntil } from 'rxjs/operators';
import { combineLatest, merge, Observable, Subject } from 'rxjs';
import { AnalyticsService, fromMatchMedia } from 'smartauction-frontend-micro-library';
import {
  AuctionItemSell,
  BatchPostingService,
  ComponentConfig,
  Configuration,
  ConfigurationState,
  DateUtilsService,
  LaunchDarklyService,
  Organization,
  SearchAuctionItems,
  SearchFiltersState,
  SearchResultAuctionItemList,
  SellConfigurationState,
  SellSearchState,
  SellToggleState,
  SetSelectedTenant,
  StatusFilter,
  TenantFilter,
  ToggleStateService,
  UpdateInventoryItemsPerPage,
  UpdateListViewToggle,
  UserProfile
} from 'remarketing-angular-library';
import { ScrollPositionService } from '../../sell-shared/services/scroll-position.service';
import { NavigationStart, Router } from '@angular/router';
import {
  ActionSubmittedModalComponent,
  ActionSubmittingModalComponent,
  CommonBannerComponent,
  ErrorMessageComponent,
  PaginationComponent
} from 'remarketing-ui-components-lib';
import { InventoryExportService } from '../services/inventory-export.service';
import { InventoryExportServiceProvider } from '../services/inventory-export.service.provider';
import { get as _get } from 'lodash';
import { SearchFiltersService } from '../services/search-filters.service';
import { ErrorModalComponent } from '@ally/remarketing/shared/ui';
import { LoadingProgress } from '../../../../app/enums/loadingProgress.enum';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { UtilityBarComponent } from '../components/utility-bar/utility-bar.component';
import { VehicleCardComponent } from '../components/vehicle-card/vehicle-card.component';
import { chunkArrayBySize } from '../../../../../src/app/shared/utils/util';
import { MultiOfferActionBannerInfo } from '@ally/remarketing/shared/services';
import { MultiActionOfferBanners } from '../../../../vehicles/components/common-list/common-list.component';
import { SharedListMultiActionModalComponent } from '../../../../shared/components/offer-modals/shared-list-multi-action-modal/shared-list-multi-action-modal.component';
import { SidebarComponent } from '../components/sidebar/sidebar.component';
import { ManageHeaderComponent } from '../components/manage-header/manage-header.component';
import { ActiveVehiclesCountComponent } from '../components/active-vehicles-count/active-vehicles-count.component';
import { CommonModule } from '@angular/common';
import { MultiVinErrorComponent } from '../components/multi-vin-error/multi-vin-error.component';
import { InventorySkeletonComponent } from '../components/inventory-skeleton/inventory-skeleton.component';
import { ListViewInventoryItemComponent } from '../components/list-view/list-view-inventory-item.component';
import { SwVehicleContainerHookComponent } from '../../../../shared/components/stockwave/sw-vehicle-container-hook/sw-vehicle-container-hook.component';
import { ActionListComponent } from '../components/action-list/action-list.component';

@Component({
    selector: 'lib-layout',
    templateUrl: './layout.component.html',
    styleUrls: ['./layout.component.scss'],
    providers: [ToggleStateService, InventoryExportServiceProvider],
    imports: [
        SharedListMultiActionModalComponent,
        TranslocoDirective,
        ManageHeaderComponent,
        CommonBannerComponent,
        ActiveVehiclesCountComponent,
        SidebarComponent,
        UtilityBarComponent,
        CommonModule,
        MultiVinErrorComponent,
        InventorySkeletonComponent,
        ErrorMessageComponent,
        ListViewInventoryItemComponent,
        SwVehicleContainerHookComponent,
        PaginationComponent,
        ActionSubmittingModalComponent,
        ActionSubmittedModalComponent,
        ErrorModalComponent,
        ActionListComponent,
        VehicleCardComponent
    ]
})
export class LayoutComponent implements OnInit, OnDestroy {
  static readonly scrollTag = 'LayoutComponent';
  @Select(SellSearchState.searchResults) searchResults$: Observable<AuctionItemSell[]>;
  @Select(SellSearchState.searchResponse) searchResponse$: Observable<SearchResultAuctionItemList>;
  @Select(ConfigurationState.configuration) configuration$: Observable<Configuration>;
  @Select(ConfigurationState.coreComponentConfig('sell')) sellConfiguration$: Observable<Partial<ComponentConfig>>;
  @Select(SearchFiltersState.getStatusFilter) searchStatusFilter$: Observable<StatusFilter>;
  @Select(SearchFiltersState.singleListSelected) singleListSelected$: Observable<boolean>;
  @Select(SellToggleState.selectedVehicles) sellSelectedVehicles$: Observable<AuctionItemSell[]>;
  @Select(SellSearchState.searchError) searchError$: Observable<any>;
  @Select(SellSearchState.missingVins) missingVins$: Observable<string[]>;
  @Select(SellSearchState.missingStockNumbers) missingStockNumbers$: Observable<string[]>;
  @ViewChild(ActionSubmittedModalComponent) actionSubmittedModal: ActionSubmittedModalComponent;
  @ViewChild(ActionSubmittingModalComponent) actionSubmittingModal: ActionSubmittingModalComponent;
  @ViewChild(ErrorModalComponent) errorModal: ErrorModalComponent;
  @ViewChild(UtilityBarComponent) utilityBar: UtilityBarComponent;
  @ViewChild(VehicleCardComponent) vehicleCard: VehicleCardComponent;
  @ViewChild(SharedListMultiActionModalComponent) sharedListMultiActionModal: SharedListMultiActionModalComponent;
  @ViewChild(SidebarComponent) sidebar: SidebarComponent;

  sellVehiclesToggled: AuctionItemSell[];
  selectedVehicleStatus: StatusFilter;

  userType = 'SELLER';
  pagination: any;
  destroyed$ = new Subject();
  sellSearchState$: Observable<LoadingProgress>;
  count: number;
  userProfile: UserProfile;
  isExporting = false;
  exportActionText: string;
  exportActionSuccessText = '';
  hasExportActionError = false;
  mobileFilter = false;
  isMobileLayout$ = fromMatchMedia('(max-width: 767px)');
  vehicleCardCommon$: Observable<{
    isMobileLayout: boolean;
    tenantName: string;
    tenantUuid: string;
    isServiceFeeVisible: boolean;
    statusFilter: StatusFilter;
  }>;
  multiVinErrors$: Observable<{
    hasError: boolean;
    missingVins: string[];
    missingStockNumbers: string[];
    sellSearchState: LoadingProgress.LOADING | LoadingProgress.LOADED | LoadingProgress.ERROR;
  }>;
  isDesktopLayout: boolean;
  isMobileLayout: boolean;
  isListView = false;
  singleListSelected: boolean;
  multiOfferActionSuccess: MultiActionOfferBanners;
  multiOfferActionErrors: MultiActionOfferBanners[];

  displayText: string;
  vehicleTextTranslations = {
    textResult: '',
    textActive: '',
    textReserve: '',
    textSold: '',
    textHold: '',
    textRemoved: '',
    textShared: '',
    textCustomOffer: '',
    textAll: ''
  };

  tableHeaderTranslations = {
    textGrounded: ''
  };

  organization = this.store.selectSnapshot(_state => _get(_state, 'org.organization', {} as Organization));
  userUuid = this.store.selectSnapshot(_state => _state.user.userProfile.userUuid);
  bsoUuid: string;

  modalId = 'bso-alert-modal';

  accountManagerRole: boolean;
  textSold: string;
  textRemoved: string;

  vehicleList$ = this.vehicleList;
  disableSelectAll: boolean;

  private window: Window;
  transaltedObject: any;
  auctionSubmittedTransl: any;
  tenantName: string;
  sellerCommentsEnabled: boolean;
  isFilterByInServiceVehicles = false;

  get isInServiceVehiclesEnabled(): boolean {
    return this._launchDarklyService.getFeatureFlag('platform-inServiceVehiclesEnabled', false);
  }

  get isListViewDefault() {
    return this._launchDarklyService.getFeatureFlag('sell-manage-inventory-default-cardview', false);
  }

  get isIsvReservationHaltEnabled(): boolean {
    return this._launchDarklyService.getFeatureFlag('sell-reservationHaltEnabled', false);
  }

  get isIsvFileUploadHaltEnabled(): boolean {
    return this._launchDarklyService.getFeatureFlag('sell-fileProcessingReservationHaltEnabled', false);
  }

  get isVehicleFilterTypeISV() {
    if (this.isFilterByInServiceVehicles) {
      return (
        this.selectedVehicleStatus === StatusFilter.RESERVED_VEHICLES ||
        this.selectedVehicleStatus === StatusFilter.ACTIVE_VEHICLES ||
        this.selectedVehicleStatus === StatusFilter.REMOVED_VEHICLES ||
        this.selectedVehicleStatus === StatusFilter.ALL_VEHICLES ||
        this.selectedVehicleStatus === StatusFilter.HOLD_VEHICLES ||
        this.selectedVehicleStatus === StatusFilter.SHARED_VEHICLES ||
        this.selectedVehicleStatus === StatusFilter.SOLD_VEHICLES
      );
    }
  }

  get filterStatusName() {
    const isInServiceVehiclesEnabled = this._launchDarklyService.getFeatureFlag(
      'platform-inServiceVehiclesEnabled',
      false
    );
    const filterStatus = this.store.selectSnapshot(SellConfigurationState.filterStatus);
    if (isInServiceVehiclesEnabled && filterStatus === StatusFilter.SHARED_VEHICLES) {
      return StatusFilter.INVOICED_VEHICLES;
    }
    return filterStatus;
  }

  constructor(
    private store: Store,
    @Inject('window') window: any,
    private analyticsService: AnalyticsService,
    private scrollPositionService: ScrollPositionService,
    private router: Router,
    private inventoryExportService: InventoryExportService,
    private searchFilterService: SearchFiltersService,
    private readonly dateUtilsService: DateUtilsService,
    private readonly _launchDarklyService: LaunchDarklyService,
    private readonly translocoService: TranslocoService,
    private readonly batchPostingService: BatchPostingService
  ) {
    this.window = window as Window;
    const loadingState$ = this.store.select(SellSearchState.loading);
    const loading$ = loadingState$.pipe(
      filter(loading => loading),
      mapTo<boolean, LoadingProgress.LOADING>(LoadingProgress.LOADING)
    );
    const loaded$ = loadingState$.pipe(
      filter(loading => !loading),
      mapTo<boolean, LoadingProgress.LOADED>(LoadingProgress.LOADED)
    );
    const error$ = this.store.select(SellSearchState.searchError).pipe(
      filter(err => !!err),
      mapTo<boolean, LoadingProgress.ERROR>(LoadingProgress.ERROR)
    );
    this.sellSearchState$ = merge(loading$, loaded$, error$).pipe(shareReplay(1)) as any;

    this.scrollPositionService.events$
      .pipe(
        filter(e => e.tag === LayoutComponent.scrollTag),
        take(1),
        delay(100)
      )
      .subscribe(({ scrollTo }) => {
        /*
          Edge doesn't support scrolling by an object of { top: number, left: number }
          so we have to scroll by passing the values into window.scrollTo directly
          https://caniuse.com/#feat=mdn-api_scrolltooptions
         */
        const { top = 0, left = 0 } = scrollTo;
        this.window.scrollTo(left, top);
      });
  }

  ngOnInit() {
    this.isListView = this.store.selectSnapshot(SellConfigurationState.isListViewToggled);
    if (this.isListViewDefault) {
      this.store.dispatch(new UpdateListViewToggle(true));
    }
    this.singleListSelected$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(singleListSelected => (this.singleListSelected = singleListSelected));
    this.getTranslations();
    this.searchStatusFilter$.pipe(takeUntil(this.destroyed$)).subscribe(statusFilter => {
      this.selectedVehicleStatus = statusFilter;
      this.setPageHeader(this.selectedVehicleStatus);
    });

    const initialInventoryItemsPerPage = this.store.selectSnapshot(SellConfigurationState.inventoryItemsPerPage);
    const currentPage = this.paginationChecks();
    this.pagination = {
      currentPage: currentPage ? currentPage : 1,
      perPage: initialInventoryItemsPerPage ? initialInventoryItemsPerPage : 10,
      maxPages: 7
    };

    // Set data based on the search response
    this.searchResponse$
      .pipe(
        takeUntil(this.destroyed$),
        filter(res => !!res)
      )
      .subscribe(searchResponse => {
        this.count = searchResponse.totalItems;
        this.disableSelectAll = false;
        this.pagination = {
          ...this.pagination,
          totalItems: searchResponse.totalItems,
          perPage: searchResponse.itemsPerPage
        };

        // format vehicle uuids and send trackPageLoad
        const itemsIds = searchResponse.data ? searchResponse.data.map(item => item.inventoryItemUuid) : [];
        const splitArrays = chunkArrayBySize(itemsIds, 1);
        const splitRecs = splitArrays.join(' | ');
        const spacedRecs = splitRecs.replace(/,/g, ' ');
        this.analyticsTrackPageView(spacedRecs);
        this.getVehicleFilterType();
      });

    this.userProfile = this.store.selectSnapshot(state => {
      if (state.user) {
        return state.user.userProfile;
      } else {
        return null;
      }
    });

    const userRoles: string[] = this.store.selectSnapshot(_state => _state.user.userProfile.fineGrainedRoles);
    this.accountManagerRole = userRoles?.filter(role => role.includes('Account_Manager')).length > 0;

    this.router.events
      .pipe(
        filter(e => e instanceof NavigationStart),
        takeUntil(this.destroyed$)
      )
      .subscribe(() => {
        this.scrollPositionService.addRestoreScrollListener(LayoutComponent.scrollTag, /inventory\/.*$/, {
          top: this.window.scrollY
        });
      });

    this.tenantName = _get(this.store.selectSnapshot(ConfigurationState.configuration), 'tenantName', '');

    this.vehicleCardCommon$ = combineLatest([
      this.configuration$,
      this.isMobileLayout$,
      this.sellConfiguration$,
      this.searchStatusFilter$
    ]).pipe(
      map(([config, isMobileLayout, sellConfiguration, statusFilter]) => {
        return {
          isMobileLayout: isMobileLayout.matches,
          tenantName: _get(config, 'tenantName', null),
          tenantUuid: _get(config, 'tenantUuid', null),
          isServiceFeeVisible: _get(sellConfiguration, 'fields["service-fee"].visible'),
          statusFilter
        };
      })
    );

    this.multiVinErrors$ = combineLatest([this.missingVins$, this.missingStockNumbers$, this.sellSearchState$]).pipe(
      map(val => {
        const [missingVins = [], missingStockNumbers = [], sellSearchState] = val;
        const hasError = missingVins.length > 0 || missingStockNumbers.length > 0 || false;

        return {
          hasError,
          missingVins,
          missingStockNumbers,
          sellSearchState
        };
      })
    );

    this.sellSelectedVehicles$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(vehiclesToToggle => (this.sellVehiclesToggled = vehiclesToToggle));

    this.sellerCommentsEnabled = this._launchDarklyService.getFeatureFlag('sell-sellerCommentsEnabled', false);
    this.detectIfDesktop();
  }

  get isSellerBulkSaleFeature(): boolean {
    return (
      this._launchDarklyService.getFeatureFlag('directSales-seller-enabled', false) &&
      this._launchDarklyService.getFeatureFlag('directSales-seller-createEnabled', false)
    );
  }

  get tenantUuid() {
    return this.store.selectSnapshot(_state => _state.user.userProfile.tenantUuid);
  }

  get vehicleList() {
    return combineLatest([this.searchResults$]).pipe(
      filter(([searchResult]) => !!searchResult),
      map(([searchResult]) => {
        return searchResult.map(result => ({
          auctionItem: result,
          hadMultiOfferActionSuccess: false
        }));
      })
    );
  }

  get showSearchTip() {
    let searchTip: string;
    const request = this.store.selectSnapshot(SellSearchState.searchRequest);
    const statusFilter = request.filterByStatus;
    switch (statusFilter) {
      case 'SOLD':
        searchTip = this.textSold;
        break;
      case 'REMOVED':
        searchTip = this.textRemoved;
        break;
      default:
        searchTip = '';
        break;
    }
    return searchTip;
  }

  paginationChecks() {
    this.searchFilterService.resetPagination.subscribe(reset => {
      if (reset) {
        this.pagination = {
          ...this.pagination,
          currentPage: 1
        };
      }
    });
    const currentPageInStore = this.store.selectSnapshot(SellConfigurationState.returnToInventory);
    let currentPage = 1;
    if (currentPageInStore !== 1) {
      currentPage = currentPageInStore;
    }
    return currentPage;
  }

  updateListViewToggle(isListView: boolean) {
    this.isListView = isListView;
    this.store.dispatch(new UpdateListViewToggle(this.isListView));
  }

  setPageHeader(statusFilter) {
    switch (statusFilter) {
      case StatusFilter.ACTIVE_VEHICLES:
        this.displayText = this.vehicleTextTranslations.textActive;
        break;
      case StatusFilter.RESERVED_VEHICLES:
        this.displayText = this.vehicleTextTranslations.textReserve;
        break;
      case StatusFilter.SOLD_VEHICLES:
        this.displayText = this.vehicleTextTranslations.textSold;
        break;
      case StatusFilter.HOLD_VEHICLES:
        this.displayText = this.vehicleTextTranslations.textHold;
        break;
      case StatusFilter.REMOVED_VEHICLES:
        this.displayText = this.vehicleTextTranslations.textRemoved;
        break;
      case StatusFilter.SHARED_VEHICLES:
        this.displayText = this.isInServiceVehiclesEnabled && !this.isVehicleFilterTypeISV
          ? this.vehicleTextTranslations.textCustomOffer : this.vehicleTextTranslations.textShared;
        break;
      case StatusFilter.ALL_VEHICLES: // display 'Vehicle Results' instead of 'All Vehicles'
        this.displayText = this.vehicleTextTranslations.textResult;
        break;
    }
  }

  isVehicleToggled(auctionItemUuid: string) {
    return this.sellVehiclesToggled.some(vehicle => vehicle.auctionItemUuid === auctionItemUuid);
  }

  handleChangePage(page: number) {
    const currentRequest = this.store.selectSnapshot(SellSearchState.searchRequest);

    const payload = {
      ...currentRequest,
      page: page - 1
    };

    this.store.dispatch(new SearchAuctionItems(payload));
    this.window.requestAnimationFrame(() => {
      this.window.scrollTo(0, 0);
    });
    this.updateCurrentPage(page);
  }

  private updateCurrentPage(currentPage: number) {
    this.pagination = {
      ...this.pagination,
      currentPage
    };
  }

  handleChangePerPage(perPage: number) {
    this.store.dispatch(new UpdateInventoryItemsPerPage(perPage));
    const currentRequest = this.store.selectSnapshot(SellSearchState.searchRequest);

    const payload = {
      ...currentRequest,
      page: 0,
      itemsPerPage: perPage
    };

    this.store.dispatch(new SearchAuctionItems(payload));
    this.window.requestAnimationFrame(() => {
      this.window.scrollTo(0, 0);
    });
    this.updatePerPage(perPage);
  }

  private updatePerPage(perPage: number) {
    this.pagination = {
      ...this.pagination,
      perPage
    };
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  dataTrackScrollNumber(index) {
    if (index % 10 === 0) {
      return index;
    }
    return null;
  }

  onExport() {
    this.hasExportActionError = false;
    this.isExporting = true;
    this.actionSubmittingModal.show();
    const request = this.store.selectSnapshot(SellSearchState.searchRequest);
    const response = this.store.selectSnapshot(SellSearchState.searchResponse);
    const sellSearchExportLimit = this.store.selectSnapshot(ConfigurationState.sellSearchExportLimit);

    const { totalItems } = response;
    this.getExportTextTranslations(totalItems, sellSearchExportLimit);

    setTimeout(() => {
      const text = document.getElementsByClassName('action-submitting__tagline');
      if (text.length > 0) {
        const p = text[0].getElementsByTagName('p');
        if (p.length > 0) {
          const sr = document.createElement('span');
          sr.innerText = this.exportActionText;
          sr.className = 'visually-hidden';
          sr.setAttribute('aria-live', 'assertive');
          sr.setAttribute('tabindex', '-1');
          p[0].appendChild(sr);
        }
      }
    }, 100);

    this.inventoryExportService
      .exportInventoryResults(request, sellSearchExportLimit, response.totalItems)
      .pipe(
        finalize(() => {
          this.isExporting = false;
          this.exportActionText = '';
          this.actionSubmittingModal.close();
          if (!this.hasExportActionError) {
            this.actionSubmittedModal.show();
          }
        })
      )
      .subscribe(
        () => {},
        () => {
          this.hasExportActionError = true;
          this.actionSubmittingModal.close();
          alert('Failed to export search results');
        }
      );
  }

  trackVehicleById(
    index: number,
    result: { auctionItem: AuctionItemSell; bsoSelected: boolean; bsoMode: boolean; partOfBso: boolean }
  ) {
    return result.auctionItem.auctionItemUuid;
  }

  @HostListener('window:resize', ['$event'])
  onResize(event) {
    this.detectIfDesktop();
  }

  detectIfDesktop() {
    this.isDesktopLayout = window.matchMedia('(min-width:1201px)').matches;
    this.isMobileLayout = window.matchMedia('(max-width: 768px)').matches;
  }

  isAllAvailableSelected(arr, arr2) {
    return arr.every(i => arr2.includes(i));
  }

  focusOnElement() {
    this.utilityBar.exportButton.nativeElement.focus();
  }

  get isSellUpdateInventoryFeatureEnabled(): boolean {
    return this._launchDarklyService.getFeatureFlag('sell-updateInventoryEnabled', false);
  }

  get areSellCheckboxesEnabled(): boolean {
    return this._launchDarklyService.getFeatureFlag('sell-manageInventorySelectionEnabled', false);
  }

  get isListViewFeatureEnabled(): boolean {
    return this._launchDarklyService.getFeatureFlag('sell-listViewEnabled', true);
  }

  get isSellReportingFeatureEnabled(): boolean {
    return this._launchDarklyService.getFeatureFlag('sell-saleReportsEnabled', false);
  }

  get areMultipleTenantsSelected(): boolean {
    const initialTenantUuid = this.sellVehiclesToggled[0]?.auctionItemTenantUuid;
    return (
      this.sellVehiclesToggled.length > 1 &&
      this.sellVehiclesToggled.some(vehicle => vehicle.auctionItemTenantUuid !== initialTenantUuid)
    );
  }

  getExportTextTranslations(totalItems, sellSearchExportLimit) {
    const keyParams = {
      'UIComponents.auctionSubmitted.exportActionSearchTextSingular': {
        auctionSummaryExportLimit: sellSearchExportLimit
      },
      'UIComponents.auctionSubmitted.exportActionSearchTextPlural': {
        resultCount: totalItems,
        auctionSummaryExportLimit: sellSearchExportLimit
      },
      'common.labelExportSuccessful': {}
    };

    this.translocoService
      .selectTranslateObject(keyParams)
      .pipe(take(1))
      .subscribe(translatedObj => {
        this.exportActionText = totalItems === 1 ? translatedObj[0] : translatedObj[1];
        this.exportActionSuccessText = translatedObj[2];
      });
  }

  get showSaleFeeAndSalePriceColumn() {
    return (
      this.selectedVehicleStatus === StatusFilter.SHARED_VEHICLES ||
      this.selectedVehicleStatus === StatusFilter.SOLD_VEHICLES
    );
  }

  get showTotalPriceColumn() {
    return this.selectedVehicleStatus === StatusFilter.SHARED_VEHICLES && this.singleListSelected;
  }

  showMultiActionModal(event: string) {
    this.sharedListMultiActionModal.show(event, this.sellVehiclesToggled);
  }

  completeOfferActions(event) {
    this.sellVehiclesToggled = [];
    this.sharedListMultiActionModal.close();
    this.createBanners(event);
  }

  updateVehicleStatusFromMultiOfferAction(event) {
    if (event.successPresent) {
      this.disableSelectAll = true;
      let newStatusFromAction;
      switch (event.action) {
        case 'ACCEPT':
          newStatusFromAction = StatusFilter.SOLD_VEHICLES;
          break;
        case 'DECLINE':
          newStatusFromAction = StatusFilter.ACTIVE_VEHICLES;
          break;
        case 'COUNTER':
          newStatusFromAction = StatusFilter.ACTIVE_VEHICLES;
          break;
        case 'REVIVE':
          newStatusFromAction = StatusFilter.ACTIVE_VEHICLES;
          break;
        case 'WITHDRAW':
          newStatusFromAction = StatusFilter.ACTIVE_VEHICLES;
          break;
        default:
          newStatusFromAction = event.action;
      }
      this.vehicleList$ = combineLatest([this.searchResults$]).pipe(
        filter(([searchResult]) => !!searchResult),
        map(([searchResult]) => {
          return searchResult.map(result => ({
            auctionItem: event.auctionItemUuids.includes(result.auctionItemUuid)
              ? this.updateAuctionItemStatus(result, newStatusFromAction)
              : result,
            hadMultiOfferActionSuccess: event.auctionItemUuids.includes(result.auctionItemUuid)
          }));
        })
      );
    }
  }

  updateAuctionItemStatus(searchResult, status) {
    searchResult = {
      ...searchResult,
      auctionItemStatus: status,
      isDirty: true,
      saleDate: this.dateUtilsService.localizeDateAndTime(Date.now())
    };
    return searchResult;
  }

  createBanners(event) {
    this.clearMultiActionBanners();
    this.createMultiActionBanners(event);
    this.updateVehicleStatusFromMultiOfferAction(event);
  }

  createMultiActionBanners(multiOfferBanners: MultiOfferActionBannerInfo) {
    this.translocoService
      .selectTranslateObject(multiOfferBanners.translationCodeMapping)
      .pipe(take(1))
      .subscribe(
        translate => {
          if (multiOfferBanners.successPresent) {
            this.multiOfferActionSuccess = {
              text: translate.pop(),
              showErrorIcon: false,
              showIcon: true,
              showUndo: false,
              showSuccessIcon: true,
              mode: 'confirmation'
            };
            this.analyticsService.track('impressionEvent', {
              name: this.analyticsMessageReference('ConfirmationMessageEvent'),
              contents: [
                {
                  name: this.analyticsMessageReference('ConfirmationSuccessMessageEvent'),
                  message: this.multiOfferActionSuccess.text,
                  attributes: {}
                }
              ]
            });
          }
          this.multiOfferActionErrors = translate.map((errorText: string) => {
            this.analyticsService.track('impressionEvent', {
              name: this.analyticsMessageReference('ConfirmationMessageEvent'),
              contents: [
                {
                  name: this.analyticsMessageReference('ConfirmationErrorMessageEvent'),
                  message: errorText,
                  attributes: {}
                }
              ]
            });
            return {
              text: errorText,
              showErrorIcon: true,
              showIcon: true,
              showUndo: false,
              showSuccessIcon: false,
              mode: 'confirmation'
            };
          });
        },
        error => {
          console.log(error);
        }
      );
  }

  clearMultiActionBanners() {
    this.multiOfferActionSuccess = null;
    this.multiOfferActionErrors = [];
  }

  closeSuccessBanner() {
    this.multiOfferActionSuccess = null;
  }

  closeErrorBanner(index) {
    this.multiOfferActionErrors.splice(index, 1);
  }

  analyticsMessageReference(confirmMessage: string) {
    return this.isInServiceVehiclesEnabled ? 'inService' + confirmMessage : 'cbl' + confirmMessage;
  }

  getTranslations() {
    const keyParams = {
      UIComponents: {},
      'sellModule.manageInventory': {},
      'common.labelExportSuccessful': {}
    };

    this.translocoService
      .selectTranslateObject(keyParams)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(translate => {
        this.transaltedObject = translate[0].pagination;
        this.auctionSubmittedTransl = translate[0].auctionSubmitted;
        this.textSold = translate[1].layout.textSold;
        this.textRemoved = translate[1].layout.textRemoved;
        this.vehicleTextTranslations.textActive = translate[1].vehicleTextTranslations.textActive;
        this.vehicleTextTranslations.textReserve = translate[1].vehicleTextTranslations.textReserve;
        this.vehicleTextTranslations.textSold = translate[1].vehicleTextTranslations.textSold;
        this.vehicleTextTranslations.textHold = translate[1].vehicleTextTranslations.textHold;
        this.vehicleTextTranslations.textRemoved = translate[1].vehicleTextTranslations.textRemoved;
        this.vehicleTextTranslations.textShared = translate[1].vehicleTextTranslations.textShared;
        this.vehicleTextTranslations.textCustomOffer = translate[1].vehicleTextTranslations.textCustomOffer;
        this.vehicleTextTranslations.textAll = translate[1].vehicleTextTranslations.textAll;
        this.vehicleTextTranslations.textResult = translate[1].vehicleTextTranslations.textResult;
      });
  }

  resetTenantEvent() {
    this.store.dispatch([new SetSelectedTenant(TenantFilter.ALL)]);
    this.sidebar.resetTenantFilterToAll();
  }

  getVehicleFilterType() {
    this._launchDarklyService
      .waitForLaunchDarkly()
      .pipe(take(1))
      .subscribe(ready => {
        if (ready) {
          if (this.isInServiceVehiclesEnabled) {
            this.store
              .select(SellSearchState.searchRequest)
              .pipe(takeUntil(this.destroyed$))
              .subscribe(response => {
                this.isFilterByInServiceVehicles = response.filterByInService;
                this.setPageHeader(this.selectedVehicleStatus);
              });
          }
        }
      });
  }

  analyticsTrackPageView(spacedRecs: string): void {
    this.analyticsService.trackPageLoad({
      page: {
        pageName: `${this.analyticsService.digitalData?.page?.category?.site}:Sell:Manage Inventory:${this.filterStatusName}`,
        section: 'Sell',
        site: this.analyticsService.digitalData?.page?.category?.site,
        subsection: 'Manage',
        title: 'Inventory',
        attributes: {
          vehicleList: spacedRecs ? spacedRecs : 'No Vehicles'
        }
      }
    });
  }
}
